import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  AnalyzeHierarchyPatternsInputSchema,
  type Config,
  type HierarchyAnalysisResult,
  type EntityHierarchyNode,
  type HierarchyPatternRecommendation,
} from '../types/index.js';
import { findFiles, readText } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const analyzeHierarchyPatternsTool: Tool = {
  name: 'analyze_hierarchy_patterns',
  description: `Analyze parent-child relationships in SmartStack entities and recommend optimal hierarchy patterns.

Detects:
- Direct parent-child relationships (via foreign keys)
- Self-referencing hierarchies (ParentId pointing to same entity)
- Inheritance chains (entity extending another entity)
- Collection relationships (ICollection<T>)

Recommends patterns based on usage:
- **TVF + CTE**: Deep hierarchies with frequent traversal (permissions, org charts)
- **Materialized Path**: Deep hierarchies with frequent modifications
- **Simple ParentId**: Shallow hierarchies (2-3 levels)
- **Nested Sets**: Read-heavy, rarely modified trees
- **Closure Table**: All operations need to be fast`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to analyze (default: SmartStack.app path from config)',
      },
      entityFilter: {
        type: 'string',
        description: 'Filter entities by name pattern (e.g., "User*", "*Group*")',
      },
      includeRecommendations: {
        type: 'boolean',
        default: true,
        description: 'Include pattern recommendations for each hierarchy',
      },
      outputFormat: {
        type: 'string',
        enum: ['summary', 'detailed', 'json'],
        default: 'detailed',
        description: 'Output format',
      },
    },
  },
};

export async function handleAnalyzeHierarchyPatterns(
  args: unknown,
  config: Config
): Promise<string> {
  const input = AnalyzeHierarchyPatternsInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;
  const includeRecommendations = input.includeRecommendations ?? true;
  const outputFormat = input.outputFormat || 'detailed';

  logger.info('Analyzing hierarchy patterns', { projectPath });

  const structure = await findSmartStackStructure(projectPath);

  if (!structure.domain) {
    return '# Error\n\nDomain project not found. Cannot analyze entity hierarchies.';
  }

  // Build entity graph
  const entityGraph = await buildEntityGraph(structure.domain, input.entityFilter);

  // Detect hierarchies
  const hierarchies = detectHierarchies(entityGraph);

  // Generate recommendations
  const recommendations = includeRecommendations
    ? generateRecommendations(hierarchies, entityGraph)
    : [];

  // Build result
  const result: HierarchyAnalysisResult = {
    totalEntities: entityGraph.size,
    hierarchicalEntities: hierarchies.filter(h => h.isHierarchical).length,
    selfReferencingEntities: hierarchies.filter(h => h.isSelfReferencing).length,
    maxDepth: Math.max(0, ...hierarchies.map(h => h.depth)),
    hierarchies,
    recommendations,
    circularDependencies: detectCircularDependencies(entityGraph),
  };

  return formatResult(result, outputFormat, structure.root);
}

// =============================================================================
// Entity Graph Building
// =============================================================================

interface ParsedEntity {
  name: string;
  file: string;
  baseClass: string;
  isTenantAware: boolean;
  isSystemEntity: boolean;
  parentEntity?: string;  // From baseEntity option or FK pattern
  selfReferenceField?: string;  // ParentId pointing to same entity
  childCollections: string[];  // ICollection<ChildEntity>
  foreignKeys: ForeignKeyInfo[];
}

interface ForeignKeyInfo {
  propertyName: string;
  referencedEntity: string;
  isNavigationProperty: boolean;
}

async function buildEntityGraph(
  domainPath: string,
  filter?: string
): Promise<Map<string, EntityHierarchyNode>> {
  const entityFiles = await findFiles('**/*.cs', { cwd: domainPath });
  const entityMap = new Map<string, EntityHierarchyNode>();
  const parsedEntities: ParsedEntity[] = [];

  // Step 1: Parse all entities
  for (const file of entityFiles) {
    const content = await readText(file);
    const parsed = parseEntity(content, file, domainPath);

    if (parsed && (!filter || matchesFilter(parsed.name, filter))) {
      parsedEntities.push(parsed);
    }
  }

  // Step 2: Create nodes
  for (const entity of parsedEntities) {
    entityMap.set(entity.name, {
      name: entity.name,
      file: entity.file,
      baseClass: entity.baseClass,
      isTenantAware: entity.isTenantAware,
      isSystemEntity: entity.isSystemEntity,
      parent: null,
      children: [],
      depth: 0,
      isRoot: true,
      isLeaf: true,
      isSelfReferencing: !!entity.selfReferenceField,
      selfReferenceField: entity.selfReferenceField,
      childCollections: entity.childCollections,
      foreignKeys: entity.foreignKeys,
      isHierarchical: false,
    });
  }

  // Step 3: Link relationships
  for (const entity of parsedEntities) {
    const node = entityMap.get(entity.name)!;

    // Link parent from FK relationships
    if (entity.parentEntity && entityMap.has(entity.parentEntity)) {
      const parent = entityMap.get(entity.parentEntity)!;
      node.parent = parent.name;
      node.isRoot = false;
      parent.children.push(entity.name);
      parent.isLeaf = false;
    }

    // Mark as hierarchical if has children or self-reference
    if (entity.childCollections.length > 0 || entity.selfReferenceField) {
      node.isHierarchical = true;
    }
  }

  // Step 4: Calculate depths
  for (const node of entityMap.values()) {
    node.depth = calculateDepth(node.name, entityMap, new Set());
  }

  // Step 5: Mark hierarchical based on children
  for (const node of entityMap.values()) {
    if (node.children.length > 0) {
      node.isHierarchical = true;
    }
  }

  return entityMap;
}

function parseEntity(content: string, file: string, domainPath: string): ParsedEntity | null {
  // Skip non-entity files
  const fileName = path.basename(file, '.cs');
  if (
    fileName.endsWith('Dto') ||
    fileName.endsWith('Command') ||
    fileName.endsWith('Query') ||
    fileName.endsWith('Handler') ||
    fileName.endsWith('Validator') ||
    fileName.endsWith('Exception') ||
    fileName.startsWith('I')
  ) {
    return null;
  }

  // Check for class declaration
  const classMatch = content.match(
    /public\s+(?:class|record)\s+(\w+)(?:\s*:\s*([^{]+))?/
  );
  if (!classMatch) return null;

  const entityName = classMatch[1];
  const inheritance = classMatch[2]?.trim() || '';

  // Must be an entity (inherit from BaseEntity or SystemEntity)
  const hasBaseEntity = inheritance.includes('BaseEntity');
  const hasSystemEntity = inheritance.includes('SystemEntity');
  if (!hasBaseEntity && !hasSystemEntity) return null;

  const hasITenantEntity = inheritance.includes('ITenantEntity');

  // Detect self-referencing (ParentId of same type)
  const selfRefMatch = content.match(
    new RegExp(`public\\s+(?:Guid\\??|${entityName}\\??)\\s+(Parent(?:Id)?|Parent${entityName}(?:Id)?)\\s*\\{`, 'i')
  );
  const selfReferenceField = selfRefMatch ? selfRefMatch[1] : undefined;

  // Detect child collections
  const childCollections: string[] = [];
  const collectionMatches = content.matchAll(
    /public\s+(?:virtual\s+)?ICollection<(\w+)>\s+(\w+)\s*\{/g
  );
  for (const match of collectionMatches) {
    childCollections.push(match[1]);
  }

  // Detect foreign keys
  const foreignKeys: ForeignKeyInfo[] = [];
  const fkMatches = content.matchAll(
    /public\s+Guid\s+(\w+Id)\s*\{[^}]+\}/g
  );
  for (const match of fkMatches) {
    const propName = match[1];
    // Skip self-reference
    if (selfReferenceField && propName.toLowerCase().includes('parent')) continue;
    // Extract referenced entity name
    const referencedEntity = propName.replace(/Id$/, '');
    if (referencedEntity !== entityName) {
      foreignKeys.push({
        propertyName: propName,
        referencedEntity,
        isNavigationProperty: content.includes(`public virtual ${referencedEntity}?`),
      });
    }
  }

  // Determine parent entity from foreign keys
  const parentEntity = foreignKeys.length > 0 ? foreignKeys[0].referencedEntity : undefined;

  return {
    name: entityName,
    file: path.relative(domainPath, file),
    baseClass: hasSystemEntity ? 'SystemEntity' : 'BaseEntity',
    isTenantAware: hasITenantEntity,
    isSystemEntity: hasSystemEntity,
    parentEntity,
    selfReferenceField,
    childCollections,
    foreignKeys,
  };
}

function matchesFilter(name: string, filter: string): boolean {
  const pattern = filter
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(`^${pattern}$`, 'i').test(name);
}

function calculateDepth(
  name: string,
  entityMap: Map<string, EntityHierarchyNode>,
  visited: Set<string>
): number {
  if (visited.has(name)) return -1; // Circular dependency
  const node = entityMap.get(name);
  if (!node || !node.parent) return 0;

  visited.add(name);
  const parentDepth = calculateDepth(node.parent, entityMap, visited);
  return parentDepth === -1 ? -1 : parentDepth + 1;
}

// =============================================================================
// Hierarchy Detection
// =============================================================================

function detectHierarchies(
  entityMap: Map<string, EntityHierarchyNode>
): EntityHierarchyNode[] {
  return Array.from(entityMap.values()).filter(
    node => node.isHierarchical || node.isSelfReferencing || node.children.length > 0
  );
}

function detectCircularDependencies(
  entityMap: Map<string, EntityHierarchyNode>
): string[][] {
  const cycles: string[][] = [];

  for (const [name] of entityMap) {
    const visited = new Set<string>();
    const path: string[] = [];

    function dfs(current: string): boolean {
      if (path.includes(current)) {
        const cycleStart = path.indexOf(current);
        cycles.push([...path.slice(cycleStart), current]);
        return true;
      }
      if (visited.has(current)) return false;

      visited.add(current);
      path.push(current);

      const node = entityMap.get(current);
      if (node?.parent) {
        dfs(node.parent);
      }

      path.pop();
      return false;
    }

    dfs(name);
  }

  // Remove duplicate cycles
  const unique = new Map<string, string[]>();
  for (const cycle of cycles) {
    const key = [...cycle].sort().join(',');
    if (!unique.has(key)) {
      unique.set(key, cycle);
    }
  }

  return Array.from(unique.values());
}

// =============================================================================
// Recommendations
// =============================================================================

function generateRecommendations(
  hierarchies: EntityHierarchyNode[],
  entityMap: Map<string, EntityHierarchyNode>
): HierarchyPatternRecommendation[] {
  const recommendations: HierarchyPatternRecommendation[] = [];

  for (const hierarchy of hierarchies) {
    const rec = analyzeHierarchy(hierarchy, entityMap);
    if (rec) {
      recommendations.push(rec);
    }
  }

  return recommendations;
}

function analyzeHierarchy(
  node: EntityHierarchyNode,
  entityMap: Map<string, EntityHierarchyNode>
): HierarchyPatternRecommendation | null {
  // Only analyze root hierarchical entities or self-referencing entities
  if (!node.isRoot && !node.isSelfReferencing) return null;

  // Calculate hierarchy metrics
  const depth = calculateMaxChildDepth(node, entityMap);
  const totalNodes = countDescendants(node, entityMap) + 1;
  const hasPermissionPattern = node.name.toLowerCase().includes('permission') ||
                               node.name.toLowerCase().includes('role') ||
                               node.name.toLowerCase().includes('group');

  // Determine recommended pattern
  let pattern: 'tvf-cte' | 'materialized-path' | 'simple-parentid' | 'nested-sets' | 'closure-table';
  let reason: string;
  let priority: 'critical' | 'high' | 'medium' | 'low';
  let sqlTemplate: string | undefined;

  if (node.isSelfReferencing) {
    // Self-referencing hierarchy
    if (depth > 3 || hasPermissionPattern) {
      pattern = 'tvf-cte';
      reason = `Self-referencing hierarchy with variable depth. ${hasPermissionPattern ? 'Permission/Group pattern detected - TVF recommended for recursive permission resolution.' : ''}`;
      priority = hasPermissionPattern ? 'critical' : 'high';
      sqlTemplate = generateTvfTemplate(node);
    } else if (depth <= 3) {
      pattern = 'simple-parentid';
      reason = 'Shallow self-referencing hierarchy (depth <= 3). Simple ParentId with eager loading is sufficient.';
      priority = 'low';
    } else {
      pattern = 'materialized-path';
      reason = 'Deep self-referencing hierarchy. Consider Materialized Path for efficient ancestor/descendant queries.';
      priority = 'medium';
    }
  } else if (node.children.length > 0) {
    // Parent-child relationship
    if (depth > 2) {
      pattern = 'tvf-cte';
      reason = `Multi-level parent-child hierarchy (depth: ${depth}). TVF with CTE recommended for efficient traversal.`;
      priority = 'medium';
      sqlTemplate = generateTvfTemplate(node);
    } else {
      pattern = 'simple-parentid';
      reason = `Simple two-level parent-child relationship. Standard FK relationship is optimal.`;
      priority = 'low';
    }
  } else {
    return null;
  }

  return {
    entityName: node.name,
    currentPattern: node.isSelfReferencing ? 'self-referencing' : 'parent-child',
    recommendedPattern: pattern,
    reason,
    priority,
    hierarchyDepth: depth,
    estimatedNodes: totalNodes,
    sqlTemplate,
    implementationSteps: getImplementationSteps(pattern, node),
  };
}

function calculateMaxChildDepth(
  node: EntityHierarchyNode,
  entityMap: Map<string, EntityHierarchyNode>,
  visited: Set<string> = new Set()
): number {
  if (visited.has(node.name)) return 0;
  visited.add(node.name);

  if (node.isSelfReferencing) {
    // For self-referencing, we estimate based on typical usage
    // Real depth would require runtime data analysis
    return 5; // Assume variable depth
  }

  if (node.children.length === 0) return 0;

  let maxChildDepth = 0;
  for (const childName of node.children) {
    const child = entityMap.get(childName);
    if (child) {
      const childDepth = calculateMaxChildDepth(child, entityMap, visited);
      maxChildDepth = Math.max(maxChildDepth, childDepth);
    }
  }

  return maxChildDepth + 1;
}

function countDescendants(
  node: EntityHierarchyNode,
  entityMap: Map<string, EntityHierarchyNode>,
  visited: Set<string> = new Set()
): number {
  if (visited.has(node.name)) return 0;
  visited.add(node.name);

  let count = 0;
  for (const childName of node.children) {
    const child = entityMap.get(childName);
    if (child) {
      count += 1 + countDescendants(child, entityMap, visited);
    }
  }

  return count;
}

function generateTvfTemplate(node: EntityHierarchyNode): string {
  const tableName = `${node.name}s`;
  const idField = 'Id';
  const parentField = node.selfReferenceField || 'ParentId';

  return `-- Table-Valued Function with Recursive CTE
-- Returns all ancestors/descendants for ${node.name}
CREATE OR ALTER FUNCTION [core].[fn_Get${node.name}Hierarchy]
(
    @${node.name}Id UNIQUEIDENTIFIER,
    @Direction VARCHAR(10) = 'ancestors' -- 'ancestors' or 'descendants'
)
RETURNS TABLE
AS
RETURN
(
    WITH Hierarchy AS (
        -- Base case: starting node
        SELECT
            ${idField},
            ${parentField},
            0 AS Level,
            CAST(${idField} AS VARCHAR(MAX)) AS Path
        FROM core.${tableName}
        WHERE ${idField} = @${node.name}Id
          AND IsDeleted = 0

        UNION ALL

        -- Recursive case: traverse hierarchy
        SELECT
            t.${idField},
            t.${parentField},
            h.Level + 1,
            h.Path + '/' + CAST(t.${idField} AS VARCHAR(MAX))
        FROM core.${tableName} t
        INNER JOIN Hierarchy h ON
            CASE @Direction
                WHEN 'ancestors' THEN t.${idField}
                WHEN 'descendants' THEN t.${parentField}
            END =
            CASE @Direction
                WHEN 'ancestors' THEN h.${parentField}
                WHEN 'descendants' THEN h.${idField}
            END
        WHERE t.IsDeleted = 0
          AND h.Level < 50 -- Prevent infinite recursion
    )
    SELECT
        ${idField},
        ${parentField},
        Level,
        Path
    FROM Hierarchy
);
GO

-- Usage example:
-- SELECT * FROM core.fn_Get${node.name}Hierarchy(@id, 'descendants');
-- SELECT * FROM core.fn_Get${node.name}Hierarchy(@id, 'ancestors');`;
}

function getImplementationSteps(
  pattern: string,
  node: EntityHierarchyNode
): string[] {
  switch (pattern) {
    case 'tvf-cte':
      return [
        `1. Create migration with TVF: fn_Get${node.name}Hierarchy`,
        `2. Add method to I${node.name}Repository: GetHierarchyAsync(Guid id, HierarchyDirection direction)`,
        `3. Implement using raw SQL: _context.Database.SqlQuery<${node.name}HierarchyDto>(...)`,
        `4. Create ${node.name}HierarchyDto with Id, ParentId, Level, Path`,
        `5. Add unit tests for hierarchy traversal (ancestors/descendants)`,
      ];
    case 'materialized-path':
      return [
        `1. Add Path column to ${node.name}: public string Path { get; private set; } // e.g., "/1/3/5/"`,
        `2. Add PathDepth computed column or property`,
        `3. Create index on Path for prefix queries`,
        `4. Update Create/Update methods to maintain Path`,
        `5. Query ancestors: WHERE @path LIKE Path + '%'`,
        `6. Query descendants: WHERE Path LIKE @path + '%'`,
      ];
    case 'simple-parentid':
      return [
        `1. Ensure ${node.selfReferenceField || 'ParentId'} FK is configured`,
        `2. Add navigation property: public virtual ${node.name}? Parent { get; }`,
        `3. Add children collection: public virtual ICollection<${node.name}> Children { get; }`,
        `4. Use .Include() for eager loading when needed`,
      ];
    case 'nested-sets':
      return [
        `1. Add Left and Right columns (int)`,
        `2. Implement tree rebuild on modifications`,
        `3. Query descendants: WHERE Left > @left AND Right < @right`,
        `4. Query ancestors: WHERE Left < @left AND Right > @right`,
        `5. Warning: Updates are expensive - only use for read-heavy scenarios`,
      ];
    case 'closure-table':
      return [
        `1. Create ${node.name}Closure table: (AncestorId, DescendantId, Depth)`,
        `2. Populate closure table on Create/Update`,
        `3. Query all ancestors: WHERE DescendantId = @id`,
        `4. Query all descendants: WHERE AncestorId = @id`,
        `5. Maintain closure table on hierarchy changes`,
      ];
    default:
      return [];
  }
}

// =============================================================================
// Output Formatting
// =============================================================================

function formatResult(
  result: HierarchyAnalysisResult,
  format: string,
  _rootPath: string
): string {
  if (format === 'json') {
    return JSON.stringify(result, null, 2);
  }

  const lines: string[] = [];

  lines.push('# Entity Hierarchy Analysis');
  lines.push('');

  // Summary
  lines.push('## Summary');
  lines.push('');
  lines.push(`| Metric | Value |`);
  lines.push(`|--------|-------|`);
  lines.push(`| Total Entities | ${result.totalEntities} |`);
  lines.push(`| Hierarchical Entities | ${result.hierarchicalEntities} |`);
  lines.push(`| Self-Referencing | ${result.selfReferencingEntities} |`);
  lines.push(`| Max Depth | ${result.maxDepth} |`);
  lines.push('');

  // Circular Dependencies
  if (result.circularDependencies.length > 0) {
    lines.push('## ⚠️ Circular Dependencies Detected');
    lines.push('');
    for (const cycle of result.circularDependencies) {
      lines.push(`- ${cycle.join(' → ')}`);
    }
    lines.push('');
  }

  // Hierarchies
  if (result.hierarchies.length > 0) {
    lines.push('## Detected Hierarchies');
    lines.push('');

    for (const h of result.hierarchies) {
      const icon = h.isSelfReferencing ? '🔄' : '📂';
      lines.push(`### ${icon} ${h.name}`);
      lines.push('');
      lines.push(`| Property | Value |`);
      lines.push(`|----------|-------|`);
      lines.push(`| File | \`${h.file}\` |`);
      lines.push(`| Type | ${h.isSelfReferencing ? 'Self-referencing' : 'Parent-child'} |`);
      lines.push(`| Tenant-aware | ${h.isTenantAware ? 'Yes' : 'No'} |`);
      lines.push(`| Depth | ${h.depth} |`);

      if (h.selfReferenceField) {
        lines.push(`| Self-ref field | \`${h.selfReferenceField}\` |`);
      }

      if (h.children.length > 0) {
        lines.push(`| Children | ${h.children.join(', ')} |`);
      }

      if (h.childCollections.length > 0) {
        lines.push(`| Collections | ${h.childCollections.join(', ')} |`);
      }

      lines.push('');
    }
  }

  // Recommendations
  if (result.recommendations.length > 0) {
    lines.push('## Pattern Recommendations');
    lines.push('');

    const priorityOrder = ['critical', 'high', 'medium', 'low'];
    const sorted = [...result.recommendations].sort(
      (a, b) => priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority)
    );

    for (const rec of sorted) {
      const priorityIcon = {
        critical: '🔴',
        high: '🟠',
        medium: '🟡',
        low: '🟢',
      }[rec.priority];

      lines.push(`### ${priorityIcon} ${rec.entityName}`);
      lines.push('');
      lines.push(`**Recommended Pattern**: \`${rec.recommendedPattern}\``);
      lines.push('');
      lines.push(`**Reason**: ${rec.reason}`);
      lines.push('');
      lines.push(`**Priority**: ${rec.priority.toUpperCase()}`);
      lines.push('');

      if (format === 'detailed') {
        lines.push('#### Implementation Steps');
        lines.push('');
        for (const step of rec.implementationSteps) {
          lines.push(step);
        }
        lines.push('');

        if (rec.sqlTemplate) {
          lines.push('#### SQL Template');
          lines.push('');
          lines.push('```sql');
          lines.push(rec.sqlTemplate);
          lines.push('```');
          lines.push('');
        }
      }
    }
  }

  // Legend
  lines.push('---');
  lines.push('');
  lines.push('## Pattern Legend');
  lines.push('');
  lines.push('| Pattern | Best For | Trade-offs |');
  lines.push('|---------|----------|------------|');
  lines.push('| **TVF + CTE** | Deep hierarchies, frequent traversal | Requires SQL Server, slightly more complex |');
  lines.push('| **Materialized Path** | Deep hierarchies, frequent modifications | Path maintenance overhead |');
  lines.push('| **Simple ParentId** | Shallow hierarchies (2-3 levels) | N+1 queries on deep traversal |');
  lines.push('| **Nested Sets** | Read-heavy, rarely modified | Expensive updates |');
  lines.push('| **Closure Table** | All operations fast | Storage overhead (N² entries) |');

  return lines.join('\n');
}
